import { useForm } from 'react-hook-form'
import { useCallback } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useNotification } from 'components/Notification'
import { createUserUploadRequest } from 'services/userUploadRequests'
import useRequestFormFileUpload from './useRequestFormFileUpload'
import {
  transformFormDataToRequestPayload,
  invalidateRequestQueries,
  DEFAULT_CREATE_AND_EDIT_FORM_VALUES,
} from '../utils'

/**
 * Hook for managing the create request form
 * @param {function} onClose - callback function to close the modal
 * @returns {object} form controls and handlers
 */
function useCreateRequestForm(onClose) {
  const { createNotification } = useNotification()
  const queryClient = useQueryClient()

  const formMethods = useForm({
    defaultValues: DEFAULT_CREATE_AND_EDIT_FORM_VALUES,
    mode: 'onChange',
  })

  const { control, handleSubmit, formState, setValue, reset } = formMethods

  // Use shared file upload functionality with form integration
  // Memoize the getter and setter to ensure stable references for the upload hook
  const getUrlFieldValues = useCallback(() => formMethods.getValues('urls'), [formMethods])
  const setUrlFieldValue = useCallback(value => formMethods.setValue('urls', value), [formMethods])

  const {
    pendingFiles,
    uploadedFiles,
    handleFileSelect,
    handleFileRemove,
    resetFileState,
    secureFileUpload,
  } = useRequestFormFileUpload({
    initialFiles: [],
    getUrlFieldValues,
    setUrlFieldValue,
  })

  const handleClose = () => {
    reset()
    resetFileState()
    onClose()
  }

  const createRequestMutation = useMutation(
    async formData => {
      const requestData = transformFormDataToRequestPayload(formData)
      return await createUserUploadRequest(requestData)
    },
    {
      onSuccess: data => {
        createNotification({
          message: 'Request created successfully',
          variant: 'success',
        })
        // Invalidate queries to refresh the requests list
        invalidateRequestQueries(queryClient)
        handleClose()
      },
      onError: error => {
        createNotification({
          message: error?.message || 'Failed to create request',
          variant: 'error',
        })
      },
    },
  )

  const handleFormSubmit = handleSubmit(formData => {
    createRequestMutation.mutate(formData)
  })

  const isSubmitting = createRequestMutation.isLoading

  return {
    control,
    formState,
    setValue,
    handleSubmit: handleFormSubmit,
    handleClose,
    isSubmitting,
    reset,
    // File upload functionality
    handleFileSelect,
    handleFileRemove,
    uploadedFiles,
    pendingFiles,
    secureFileUpload,
  }
}

export default useCreateRequestForm
